import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { python } from "@codemirror/lang-python";
import { html } from "@codemirror/lang-html";
import { css } from "@codemirror/lang-css";
import { TerminalSquare, Play, Send } from "lucide-react";
import { BookOpen, Code, SendHorizonal } from "lucide-react";
import {
  TECHNOLOGY_API_ROUTE,
  PLAY_EXO_API_ROUTE,
  PLAY_EXO_JS_TS_API_ROUTE,
  TEST_CODE_API_ROUTE,
  TEST_CODE_JS_TS_API_ROUTE,
  SEND_RESPONSE_EXO_API_ROUTE,
  SEND_RESPONSE_EXO_JS_TS_API_ROUTE,
  RESET_CHALLENGE_API_ROUTE,
  RESET_CHALLENGE_JS_TS_API_ROUTE,
} from "../../routes/api/api_routes";
import EditeurMobile from "./EditeurMobile";


const getLanguageExtension = (languageId) => {
  switch (languageId) {
    case "javascript":
    case "typescript":
      return javascript();
    case "python":
      return python();
    case "html":
      return html();
    case "css":
      return css();
    default:
      return javascript();
  }
};

const EditeurPratique = () => {
  const { languageId, levelId, challengeProfileId } = useParams();
  const techId = languageId;
  const navigate = useNavigate();
  const token = localStorage.getItem("access");

  const [exerciseData, setExerciseData] = useState(null);
  const [code, setCode] = useState("// Écris ton code ici");
  const [consoleOutput, setConsoleOutput] = useState("");
  const [testData, setTestData] = useState({ passed: 0, total: 0, percentage: 0 });
  const [languageName, setLanguageName] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isQuizCompleted, setIsQuizCompleted] = useState(false);
  const [finalResults, setFinalResults] = useState([]);
  const [resultFromTest, setResultFromTest] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [consoleStatus, setConsoleStatus] = useState("");   // "Excellent", "Bien", "Échec"
  const [consoleStatusColor, setConsoleStatusColor] = useState("white");
  const [consoleMessage, setConsoleMessage] = useState(""); // message secondaire
  const [isMobile, setIsMobile] = useState(false);
  const { state } = useLocation();
  const mode = state?.mode || "pratique"; // fallback par défaut
  const estimatedTime = state?.estimatedTime || 0;
  const [remainingTime, setRemainingTime] = useState(0);

  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(1); // étapes 1 → 3

  useEffect(() => {
    const hasVisited = localStorage.getItem("hasVisitedEditor");
    if (!hasVisited) {
      setShowTutorial(true);
      localStorage.setItem("hasVisitedEditor", "true");
    }
  }, []);

  const handleNextTutorialStep = () => {
  if (tutorialStep < 3) {
    setTutorialStep(prev => prev + 1);
  } else {
    setShowTutorial(false); // fin du tutoriel
  }
};

useEffect(() => {
  console.log("⏳ Temps restant :", remainingTime);
}, [remainingTime]);


useEffect(() => {
  if (mode === "evaluation") {
    const totalSeconds = estimatedTime * 60;
    setRemainingTime(totalSeconds);

    const interval = setInterval(() => {
      setRemainingTime(prevTime => {
        if (prevTime <= 1) {
          clearInterval(interval);
          handleSubmit(); // auto-submit à la fin du temps
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }
}, [mode, estimatedTime]);

const formatTime = (seconds) => {
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  return `${min.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
};


useEffect(() => {
  const checkMobile = () => {
    setIsMobile(window.innerWidth < 768); // ou une autre valeur selon ton breakpoints
  };
  checkMobile();
  window.addEventListener("resize", checkMobile);
  return () => window.removeEventListener("resize", checkMobile);
}, []);


  useEffect(() => {
    const fetchTechnologyName = async () => {
      try {
        const res = await fetch(`${TECHNOLOGY_API_ROUTE}${languageId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setLanguageName(data.name.toLowerCase());
      } catch (err) {
        console.error("Erreur récupération nom techno", err);
      }
    };
    if (languageId) {
      fetchTechnologyName();
    }
  }, [languageId, token]);

  const fetchPlay = async () => {
    const endpoint = languageName === "python"
      ? PLAY_EXO_API_ROUTE
      : PLAY_EXO_JS_TS_API_ROUTE;

    const url = `${endpoint}${challengeProfileId}/false/${techId}/${levelId}?mode=${mode}`;

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      setExerciseData(json.data.challenge_profile);
      setConsoleOutput("");
    } catch (err) {
      console.error(err);
      setConsoleOutput("Erreur chargement exercice.");
    }
  };

  useEffect(() => {
    if (languageName && challengeProfileId && languageId && levelId) {
      fetchPlay();
    }
  }, [languageName, challengeProfileId, languageId, levelId, techId, token]);

  const runCode = async () => {
    if (!exerciseData) return;

    if (!code || code.trim() === "" || code.trim() === "// Écris ton code ici") {
      setConsoleStatus("⚠️ Code vide !");
      setConsoleMessage("Veuillez écrire du code avant d’exécuter.");
      setConsoleStatusColor("orange");
      return; // On arrête ici pour éviter de lancer le test avec du vide
    }

    const { exercice } = exerciseData;
    setConsoleMessage("⏳ Exécution du code en cours...");
    let url = "";
    const isTypeScript = languageName === "typescript";
    let options = {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },

      body: JSON.stringify({
        exercice_id: exercice.id,
        resultat_code: code,
        language: languageName === "python" ? "python" : (isTypeScript ? "ts" : "js"),
      }),
    };

    if (languageName === "python") {
      url = TEST_CODE_API_ROUTE;
    } else {
      url = TEST_CODE_JS_TS_API_ROUTE;
    }

    try {
      const res = await fetch(url, options);
      const json = await res.json();
      console.log("Réponse test API:", json);

       if (json.output?.includes("Daily limit exceeded")) {
        setConsoleStatus("🚫 Limite atteinte !");
        setConsoleMessage("La limite quotidienne d'exécutions de code est dépassée.");
        setConsoleStatusColor("orange");
        return;
      }

      const { passed_tests, total_tests, success } = json;
      console.log(success)

      setResultFromTest(success);

      const percentage = total_tests > 0 ? Math.round((passed_tests / total_tests) * 100) : 0;

      setTestData({
        passed: passed_tests,
        total: total_tests,
        percentage: percentage,
      });

      let status = "";
      let message = "";
      let color = "";

      if (passed_tests === total_tests && total_tests > 0) {
        status = "Excellent !";
        message = "Vous pouvez passer au niveau suivant.";
        color = "green";
      } else if (passed_tests > 0) {
        status = "Bien !";
        message = "Vous pouvez améliorer votre code pour un meilleur résultat.";
        color = "goldenrod";
      } else {
        status = "Échec !";
        message = "Veuillez réessayer.";
        color = "red";
      }

      setConsoleStatus(status);
      setConsoleMessage(message);
      setConsoleStatusColor(color);
      setResultFromTest(success);
    } catch (err) {
      console.error(err);
      setConsoleStatus("Erreur !");
      setConsoleMessage("Une erreur est survenue pendant l’exécution.");
      setConsoleStatusColor("red");
    }
  };

  const sendResponse = async (completed = "false") => {
    if (!exerciseData) return;
    const { exercice, reponses } = exerciseData;
    const idRep = reponses[0]?.id;

    let url = "";
    let options = {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };

    const urlMode = `?mode=${mode}`;

    if (languageName === "python") {
      url = `${SEND_RESPONSE_EXO_API_ROUTE}${urlMode}`;
      options.body = JSON.stringify({
        exercice_id: exercice.id,
        challenge_profile_id: challengeProfileId,
        reponse_id: idRep,
        code,
        mode,  // En plus dans le body pour plus de robustesse
      });
    } else {
      url = `${SEND_RESPONSE_EXO_JS_TS_API_ROUTE}${urlMode}`;
      options.body = JSON.stringify({
        exercice_id: exercice.id,
        challenge_profile_id: challengeProfileId,
        reponse_id: idRep,
        completed: completed === "true",
        resultat_code: resultFromTest ? "true" : "false",
        mode,  // Pareil ici
      });
    }
    
    try {
      const res = await fetch(url, options);
      const json = await res.json();
      console.log("Réponse de l'API:", json);

      if (json.completed === true) {
        console.log("🎯 Résultats finaux:", json.details);
        setIsQuizCompleted(true);
        setExerciseData(null);
        setFinalResults(json.details || []);
        return;
      }
      else if (json.data?.challenge_profile) {
        setExerciseData(json.data.challenge_profile);
        setCode("// Écris ton code ici");
        setConsoleStatus("");
        setConsoleMessage("");
        setConsoleStatusColor("white");
        setTestData({ passed: 0, total: 0, percentage: 0 });
        setResultFromTest(false);
      } else {
        setConsoleStatus("❌ Erreur inattendue dans la réponse de l'API.");
      }
    } catch (err) {
      console.error(err);
      setConsoleStatus("Erreur lors de la soumission.");
    }
  };

  // La logique de soumission est maintenant gérée ici
  const handleConfirmSubmit = async () => {
    setShowConfirmationModal(false); // Ferme le modal de confirmation
    setConsoleStatus("⏳ Soumission en cours...");
    setShowModal(true); // Affiche le modal de chargement
    await sendResponse("true"); // Envoie la réponse à l'API
    setTimeout(() => {
      setShowModal(false);
    }, 2000);
  };

  const handleCancelSubmit = () => {
    setShowConfirmationModal(false);
    setConsoleStatus("❌ Soumission annulée. Vous pouvez continuer à travailler.");
  };

  const handleSubmit = async () => {
    // Vérifier le résultat des tests avant de soumettre
    if (resultFromTest) {
      // Si les tests sont réussis, soumettre directement
      setConsoleStatus("✅ Tests réussis. Soumission...");
      setShowModal(true);
      await sendResponse("true");
      setTimeout(() => {
        setShowModal(false);
      }, 2000);
    } else {
      // Si les tests ont échoué, afficher le modal de confirmation
      setShowConfirmationModal(true);
    }
  };

  useEffect(() => {
    if (!challengeProfileId || !languageId || !levelId) {
      console.warn("Paramètres manquants dans l’URL. Redirection...");
      navigate("/pratique", { replace: true });
    }
  }, [challengeProfileId, languageId, levelId, navigate]);

  const handleRetry = async () => {
    try {
      const resetEndpoint = languageName === "python"
        ? RESET_CHALLENGE_API_ROUTE
        : RESET_CHALLENGE_JS_TS_API_ROUTE;
      await fetch(`${resetEndpoint}${challengeProfileId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      setFinalResults([]);
      setIsQuizCompleted(false);
      setCode("// Écris ton code ici");
      setConsoleOutput("");
      setShowModal(false);
      fetchPlay();
    } catch (err) {
      console.error("Erreur lors de la réinitialisation du challenge", err);
    }
  };

  if (!exerciseData && !isQuizCompleted) {
    return (
      <div className="p-6 text-white text-center">
        {consoleOutput || "Chargement de l’exercice..."}
      </div>
    );
  }

  const exercice = exerciseData?.exercice;

  return (
    <>
      {isMobile ? (
        <EditeurMobile
          exercice={exerciseData?.exercice}
          code={code}
          setCode={setCode}
          languageId={languageId}
          runCode={runCode}
          handleSubmit={handleSubmit}
          consoleStatus={consoleStatus}
          consoleStatusColor={consoleStatusColor}
          consoleMessage={consoleMessage}
          testData={testData}
          getLanguageExtension={getLanguageExtension}
          remainingTime={remainingTime}
          mode={mode}
        />
      ) : (
        // ↪ ici tu gardes tout ton code actuel pour la version desktop
        <div className="flex flex-wrap p-2 gap-3 h-max bg-[#131c25] text-[#e0e0e0] font-sans">
        {/* Partie gauche : Enoncé + Console */}
        <div className="flex flex-col gap-3 flex-1 min-w-[320px]">
          {/* Enoncé */}
          <div
            className="flex flex-col justify-between bg-[#252e38] rounded-sm min-h-[360px]"
            style={{ border: "1px solid #3d4145" }}
          >
            <div className="p-2 overflow-auto h-full">
              <div className="flex items-center justify-between border-b border-gray-700 pb-2 mb-2">
                <h3 className="text-[#7a7c7e] text-sm font-semibold">
                  Énoncé
                </h3>
                {mode === "evaluation" && (
                  <span
                    className={`font-mono text-sm px-2 py-1 rounded ${
                      remainingTime <= 60
                        ? "bg-red-700 text-white animate-pulse"
                        : "bg-yellow-600 text-black"
                    }`}
                  >
                    ⏱️ {formatTime(remainingTime)}
                  </span>
                )}
              </div>
              <p className="text-orange-400 font-medium mb-3 whitespace-pre-wrap">{exercice?.intitule}</p>
            </div>
          </div>
          {/* Console */}
          <div
            className="bg-[#252e38] p-1 rounded-sm min-h-[100px]"
            style={{ border: "1px solid #3d4145" }}
          >
            <h3 className="text-[#7a7c7e] text-sm p-1 font-semibold flex items-center gap-1 border-b border-white/10 pb-2 mb-2">
              <TerminalSquare size={16} /> Console
            </h3>
            <div
              className="bg-[#1e1f26] p-2 rounded-sm font-mono whitespace-pre-wrap overflow-auto min-h-[85px]"
              style={{ border: "1px solid #3d4145", color: "white" }}
            >
              {consoleStatus && (
                <span style={{ color: consoleStatusColor, fontWeight: "bold" }}>
                  {consoleStatus}
                </span>
              )}
              {consoleStatus && <br />}
              {consoleMessage}
            </div>
          </div>
        </div>
        {/* Partie droite : Editeur + Résultats + Actions */}
        <div className="flex flex-col gap-3 flex-1 min-w-[320px]">
          {/* Editeur */}
          <div
            className="bg-[#252e38] p-2 rounded-sm"
            style={{ border: "1px solid #3d4145" }}
          >
            <h3 className="text-[#7a7c7e] text-sm font-semibold border-b border-white/10 pb-2 mb-2">
              Code
            </h3>
            <div
              className="bg-[#111920] rounded-sm min-h-[240px] overflow-hidden text-white"
              style={{ border: "1px solid #3d4145" }}
            >
              <CodeMirror
                value={code}
                height="240px"
                theme="dark"
                extensions={[getLanguageExtension(languageId)]}
                onChange={setCode}
                basicSetup={{
                  lineNumbers: true,
                  foldGutter: true,
                  highlightActiveLine: true,
                }}
              />
            </div>
          </div>

          {/* Résultat + Actions */}
          <div className="flex gap-3 w-full">
            {/* Résultat */}
            <div
              className="basis-full md:basis-2/3 min-h-[200px] bg-[#252e38] p-2 rounded-sm"
              style={{ border: "1px solid #3d4145" }}
            >
              <h3 className="text-[#7a7c7e] text-sm font-semibold border-b border-white/10 pb-2 mb-2">
                Résultat
              </h3>

              {/* Tests */}
              <p className="mt-1 mb-0 text-white">
                Test :{" "}
                <span style={{ color: testData.total > 0 && testData.passed === testData.total ? "white" : "#cccccc" }}>
                  {testData.total > 0 ? `${testData.passed}/${testData.total}` : "0/0"}
                </span>
              </p>

              {/* Pourcentage */}
              <p className="mt-1 mb-0 text-white">
                % de Reussite :{" "}
                <span style={{ 
                  color:
                    testData.percentage === 100
                      ? "limegreen"
                      : testData.percentage >= 50
                      ? "orange"
                      : "red"
                }}>
                  {testData.percentage}%
                </span>
              </p>
            </div>

            {/* Actions */}
            <div
              className="basis-full md:basis-1/3 min-h-[200px] bg-[#252e38] p-2 rounded-sm"
              style={{ border: "1px solid #3d4145" }}
            >
              <h3 className="text-[#7a7c7e] text-sm font-semibold border-b border-white/10 pb-2 mb-2">
                Actions
              </h3>
              <div className="flex flex-col gap-3 mt-2">
                <button
                  onClick={runCode}
                  className="bg-[#515253] text-green-600 font-bold py-2 rounded-sm hover:bg-green-600 hover:text-gray-900"
                >
                  <Play size={20} className="inline mr-1" /> Exécuter
                </button>
                <button
                  onClick={handleSubmit}
                  className="bg-[#515253] text-orange-500 font-bold py-2 rounded-sm hover:bg-orange-500 hover:text-gray-900"
                >
                  <Send size={20} className="inline mr-1" /> Soumettre
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
      )}
      {showModal && (
          <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-[#1e1f26] border border-orange-400 text-white p-6 rounded-md text-center shadow-lg">
              <h2 className="text-xl font-bold mb-4 text-orange-400">Soumission terminée !</h2>
              <p className="text-gray-300">Chargement du prochain exercice...</p>
            </div>
          </div>
        )}
        {showConfirmationModal && (
          <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-[#1e1f26] border border-red-500 text-white p-6 rounded-md text-center shadow-lg">
              <h2 className="text-xl font-bold mb-4 text-red-500">Attention !</h2>
              <p className="text-gray-300 mb-4">
                Votre code a échoué aux tests unitaires.
                Êtes-vous sûr de vouloir soumettre ?
              </p>
              <div className="flex justify-center gap-4">
                <button
                  onClick={handleConfirmSubmit}
                  className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md"
                >
                  Soumettre quand même
                </button>
                <button
                  onClick={handleCancelSubmit}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}
        {isQuizCompleted && (
          <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-60 flex items-center justify-center z-50">
            <div className="bg-[#1e1f26] border border-green-500 text-white p-6 rounded-md text-center shadow-lg max-w-md w-full">
              <h2 className="text-2xl font-bold mb-4 text-green-400">🎉 Bravo !</h2>
              <p className="text-gray-300 mb-2">Vous avez terminé tous les exercices de ce niveau.</p>

              <div className="text-left mt-4 mb-2">
                <h3 className="text-orange-400 font-semibold mb-2">Résultats :</h3>
                <ul className="text-sm space-y-1 max-h-48 overflow-y-auto">
                  {finalResults.map((res, index) => (
                    <li key={res.exercice_id}>
                      {index + 1} -{" "}
                      <span className={res.success ? "text-green-400" : "text-red-400"}>
                        {res.success ? "Réussi" : "Échec"}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="mt-4 text-lg font-semibold text-orange-300">
                Score : {Math.round((finalResults.filter(r => r.success).length / finalResults.length) * 100)} / 100
              </p>

              <div className="mt-6 flex justify-center gap-4">
                <button
                  onClick={handleRetry}
                  className="bg-blue-500 hover:bg-blue-400 text-white px-4 py-2 rounded"
                >
                  🔁 Refaire les exercices
                </button>
                <button
                  onClick={() => navigate("/pratique")}
                  className="bg-green-500 hover:bg-green-400 text-gray-900 px-4 py-2 rounded"
                >
                  ⏭️ Retour à la pratique
                </button>
              </div>
            </div>
          </div>
        )}
        {showTutorial && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <div className="bg-white text-black border border-gray-300 p-6 rounded-md text-center shadow-xl max-w-xs sm:max-w-sm w-full">

              {tutorialStep === 1 && (
                <>
                  <BookOpen size={40} className="mx-auto mb-4 text-orange-500" />
                  <h2 className="text-xl font-bold mb-2 text-orange-600">
                    Comprendre l'exercice
                  </h2>
                  <p className="mb-4 text-gray-700">
                    À gauche, tu trouveras l’<span className="font-semibold">énoncé</span> du défi.
                    Lis-le attentivement avant d'écrire ton code.
                  </p>
                  <button
                    onClick={handleNextTutorialStep}
                    className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded font-medium"
                  >
                    Suivant
                  </button>
                </>
              )}

              {tutorialStep === 2 && (
                <>
                  <Code size={40} className="mx-auto mb-4 text-green-500" />
                  <h2 className="text-xl font-bold mb-2 text-green-600">
                    Écris et exécute ton code
                  </h2>
                  <p className="mb-4 text-gray-700">
                    Utilise l’éditeur à droite pour coder ta solution.
                    Clique ensuite sur <span className="font-semibold">“Exécuter”</span> pour tester ton code.
                  </p>
                  <button
                    onClick={handleNextTutorialStep}
                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded font-medium"
                  >
                    Suivant
                  </button>
                </>
              )}

              {tutorialStep === 3 && (
                <>
                  <SendHorizonal size={40} className="mx-auto mb-4 text-orange-500" />
                  <h2 className="text-xl font-bold mb-2 text-orange-600">
                    Soumettre ta réponse
                  </h2>
                  <p className="mb-4 text-gray-700">
                    Clique sur <span className="font-semibold">“Soumettre”</span> pour valider ta solution
                    et passer à l’exercice suivant.
                  </p>
                  <button
                    onClick={handleNextTutorialStep}
                    className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded font-medium"
                  >
                    C’est parti !
                  </button>
                </>
              )}
            </div>
          </div>
        )}
    </>
  );
};

export default EditeurPratique;
